import React, { useEffect, useState } from 'react';

import ApprovalHistoryTable from 'Components/budget_change_requests/ApprovalHistoryTable';
import ApprovalStepper from 'Components/budget_change_requests/ApprovalStepper';
import BalanceImpactCallout from 'Components/budget_change_requests/BalanceImpactCallout';
import ChangeAmountSummary from 'Components/budget_change_requests/ChangeAmountSummary';
import { localDate } from 'Components/budget_change_requests/changeRequestFormat';
import PositionChanges from 'Components/budget_change_requests/PositionChanges';
import RequesterExplanation from 'Components/budget_change_requests/RequesterExplanation';
import SourceOfFundsBar from 'Components/budget_change_requests/SourceOfFundsBar';

/**
 * What an approver is deciding on for a budget change: how the amount moves,
 * which pots fund it, what approving it leaves of those pots, and where the
 * request stands in its chain.
 *
 * THE FIGURES ARE FETCHED ON OPEN, NOT READ OFF THE ROW. Contingency is drawn by
 * many locations at once, so a balance taken when the register loaded is stale
 * the moment another request is approved against the same pot — and this is the
 * figure the approver acts on. The server re-walks the whole fold per call, and
 * recomputes it again for real when the action lands.
 *
 * The layout is IB-961's read-only Budget Change Requests pop-up, rendered from
 * the same `ChangeRequestRowData` projection its table and CSV read, so the two
 * screens cannot report different money for one request.
 *
 * NO AMOUNT INPUTS. The split is system-calculated — local contingency first,
 * then central, then event profit — so approving takes no figures from the user.
 */
export default function BudgetChangeBody({ row, myViews, onExtrasChange }) {
    const [detail, setDetail] = useState(null);
    const [failed, setFailed] = useState(false);

    const record = row?.record ?? {};

    useEffect(() => {
        if (!record.id) {
            return;
        }

        setDetail(null);
        setFailed(false);

        axios
            .get(myViews.fetch_budget_change_url.replace('BUDGET_CHANGE_ID', record.id))
            .then(({ data }) => setDetail(data))
            // The endpoint sits behind the budget_change_and_contingency function
            // gate, so this is reached on an event where the function was switched
            // off with requests still pending. Nothing is fabricated in its place:
            // the approver is told the figures are missing, and Approve is withheld
            // because approving a draw on contingency without having seen what it
            // draws from is not a decision. Reject needs none of them and stays.
            .catch(() => {
                setFailed(true);
                onExtrasChange?.({ totalsUnavailable: true });
            });
    }, [record.id]);

    if (failed) {
        return (
            <p className="modal__heading tw-text-red">
                {$t('The figures for this budget change could not be loaded, so it cannot be approved right now. You can still reject it.')}
            </p>
        );
    }

    if (!detail) {
        return <p className="modal__text">{$t('Loading…')}</p>;
    }

    const request = detail.row ?? {};
    const funding = request.funding ?? {};

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <ChangeAmountSummary row={request} />

            <div>
                <div className="bc-eyebrow">{$t('Source of funds')}</div>
                <SourceOfFundsBar funding={funding} category={request.category} profitEnabled={!!detail.profit_enabled} />
            </div>

            {/* THE SENTENCES ARE NOT SPLIT AROUND THE FIGURES. Interpolating a
                styled element mid-sentence would mean three fragment keys per
                line, which Arabic cannot reorder — so a balance the approval
                takes below zero colours the whole callout instead, using the
                same whole-sentence keys the budget change screen already has.
                The callout itself is shared with that screen (BalanceImpactCallout)
                so the two cannot show different money, or diverge on when this
                warning applies, for one request. */}
            <BalanceImpactCallout funding={funding} negativeWarning={goesNegative(funding)} />

            <div className="bc-kv">
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Change')}</span>
                    {request.change_label}
                </div>
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Group')}</span>
                    {request.group_label}
                </div>
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Department / cost centre')}</span>
                    {request.location_label}
                </div>
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Requested by')}</span>
                    {request.requested_by}, {localDate(request.submitted_at)}
                </div>
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Description')}</span>
                    {request.description}
                </div>
                <div className="bc-kv__cell">
                    <span className="bc-kv__key">{$t('Reason')}</span>
                    {request.reason}
                </div>
            </div>

            <PositionChanges row={request} />

            <RequesterExplanation row={request} />

            <div>
                <div className="bc-eyebrow">{$t('Approval progress')}</div>
                <ApprovalStepper approval={request.approval} />
            </div>

            {/* The submission first, then each decision. ApprovalDetailModal leaves
                its generic approvers table out for budget changes in favour of this. */}
            <ApprovalHistoryTable history={request.history} />
        </div>
    );
}

/**
 * Whether approving would leave any shown pot below zero.
 *
 * The approver is NOT blocked — an over-drawn contingency is a decision they are
 * allowed to make. It is coloured because a minus sign inside a formatted figure
 * is what gets approved by accident.
 */
function goesNegative(funding) {
    // Only the figures this callout actually renders: a cumulative remaining is
    // withheld on an actioned row, and colouring the block over a number nobody
    // can see reads as a defect.
    const shown = [funding.balance_after, funding.central_balance_after];

    if (!funding.at_approval && funding.cumulative_count > 0) {
        shown.push(funding.cumulative_remaining);
    }

    return shown.some((value) => typeof value === 'number' && value < 0);
}
